import DOMPurify from "@/vendor/dompurify.es.js";

export function escapeHtml(value) {
    if (value === null || value === undefined) {
        return "";
    }

    return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

export function sanitizeHtml(value) {
    return escapeHtml(value);
}

function decodeCommonHtmlEntities(value) {
    return String(value)
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&amp;/g, "&");
}

export function decodeHtmlEntities(value) {
    if (value === null || value === undefined) {
        return "";
    }

    return decodeCommonHtmlEntities(value);
}

function decodeGtEntities(value) {
    if (typeof value !== "string") {
        return value;
    }

    if (value.indexOf("&gt;") === -1 && value.indexOf("&GT;") === -1) {
        return value;
    }

    return value.replace(/&gt;/gi, ">");
}

export function decodeGtEntitiesInPayload(payload) {
    const seen = typeof WeakSet !== "undefined" ? new WeakSet() : null;

    function walk(node) {
        if (node === null || node === undefined || typeof node !== "object") {
            return;
        }

        if (seen) {
            if (seen.has(node)) {
                return;
            }
            seen.add(node);
        }

        if (Array.isArray(node)) {
            for (let i = 0; i < node.length; i++) {
                if (typeof node[i] === "string") {
                    node[i] = decodeGtEntities(node[i]);
                } else {
                    walk(node[i]);
                }
            }
            return;
        }

        Object.keys(node).forEach(key => {
            const val = node[key];
            if (typeof val === "string") {
                node[key] = decodeGtEntities(val);
            } else if (val && typeof val === "object") {
                walk(val);
            }
        });
    }

    walk(payload);
    return payload;
}

export function formatMultiline(value) {
    if (value === null || value === undefined || value === "") {
        return "";
    }

    return String(value)
        .split(/\r\n|\n|\r/)
        .map(line => escapeHtml(line.trim()))
        .join("<br>");
}

export function formatCommaList(value) {
    if (value === null || value === undefined || value === "") {
        return "";
    }

    return String(value)
        .split(",")
        .map(part => escapeHtml(part.trim()))
        .join("<br>");
}

export function formatPlainTextHtml(value) {
    if (value === null || value === undefined || value === "") {
        return "";
    }

    const decoded = decodeCommonHtmlEntities(value);

    return escapeHtml(decoded).replace(/\r\n|\n|\r/g, "<br>");
}

const RICH_HTML_ALLOWED_TAGS = [
    "p", "br", "strong", "em", "b", "i", "u", "ul", "ol", "li", "a",
    "h1", "h2", "h3", "h4", "h5", "h6",
    "table", "thead", "tbody", "tr", "td", "th",
    "span", "div", "blockquote", "hr", "img"
];

const RICH_HTML_ALLOWED_ATTR = [
    "href", "target", "rel", "class", "src", "alt", "title", "width", "height",
    "colspan", "rowspan"
];

let domPurifyHooksRegistered = false;

function registerDomPurifyHooks() {
    if (domPurifyHooksRegistered || typeof window === "undefined") {
        return;
    }

    domPurifyHooksRegistered = true;

    DOMPurify.addHook("afterSanitizeAttributes", (node) => {
        if (node.tagName === "A" && node.hasAttribute("href")) {
            node.setAttribute("target", "_blank");
            node.setAttribute("rel", "noopener noreferrer");
        }
    });
}

function htmlToPlainText(html) {
    if (typeof document !== "undefined") {
        const container = document.createElement("div");
        container.innerHTML = html;
        return (container.textContent || container.innerText || "").trim();
    }

    return String(html).replace(/<[^>]*>/g, "").trim();
}

export function sanitizeRichHtml(value) {
    if (value === null || value === undefined || value === "") {
        return "";
    }

    registerDomPurifyHooks();

    return DOMPurify.sanitize(String(value), {
        ALLOWED_TAGS: RICH_HTML_ALLOWED_TAGS,
        ALLOWED_ATTR: RICH_HTML_ALLOWED_ATTR,
        ALLOW_DATA_ATTR: false
    });
}

export function sanitizeRichHtmlExcerpt(value, maxLength = 300) {
    if (value === null || value === undefined || value === "") {
        return "";
    }

    const clean = sanitizeRichHtml(value);
    if (!clean) {
        return "";
    }

    const text = htmlToPlainText(clean);
    if (text.length <= maxLength) {
        return "<br><br>" + clean;
    }

    return "<br><br>" + escapeHtml(text.substring(0, maxLength)) + "...";
}
